import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import { useSlotTimer } from '../../hooks/useSlotTimer';
import { useCall } from '../../context/CallContext';
import ReviewModal from '../../components/common/ReviewModal';
import {
  getChatHistory,
  uploadChatFile,
  getSlotStatus,
  deleteChatMessage,
} from '../../services/patientService';
import { formatCountdown } from '../../utils/helpers';


// ── Main ChatPage ─────────────────────────────────────────────────────────

// ── Message Bubble ─────────────────────────────────────────────────────────

function MessageBubble({ msg, isMe, onDeleteForEveryone }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const holdTimer = React.useRef(null);
  const menuRef   = React.useRef(null);

  const t = new Date(msg.timestamp || msg.createdAt)
    .toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit', hour12: true });

  const msgAge = Date.now() - new Date(msg.timestamp || msg.createdAt).getTime();

  // Close menu on outside click
  React.useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler); };
  }, [menuOpen]);

  // Long press handlers for mobile
  const startHold = () => { holdTimer.current = setTimeout(() => setMenuOpen(true), 500); };
  const cancelHold = () => { clearTimeout(holdTimer.current); };

  // If already deleted for everyone
  if (msg.deletedForEveryone) {
    return (
      <div className={`flex mb-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
        <div className="flex items-center gap-1.5 px-3 py-2 rounded-2xl border border-border bg-white/60">
          <i className="fa-solid fa-ban text-text-muted text-xs" />
          <p className="text-xs text-text-muted italic">This message was deleted</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex mb-2 ${isMe ? 'justify-end' : 'justify-start'} relative`}>
      <div
        className={`max-w-[75%] rounded-2xl px-3 py-2 select-none ${
          isMe ? 'bg-primary text-white rounded-br-sm'
               : 'bg-white text-text-primary rounded-bl-sm shadow-sm border border-border'
        }`}
        onTouchStart={isMe ? startHold : undefined}
        onTouchEnd={isMe ? cancelHold : undefined}
        onTouchMove={isMe ? cancelHold : undefined}
        onContextMenu={isMe ? (e) => { e.preventDefault(); setMenuOpen(true); } : undefined}
      >
        {msg.type === 'text'   && <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.message}</p>}
        {msg.type === 'image'  && <img src={msg.fileUrl} alt="img" className="rounded-xl max-w-full max-h-48 object-cover cursor-pointer" onClick={() => window.open(msg.fileUrl, '_blank')} />}
        {msg.type === 'pdf'    && <a href={msg.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2"><i className="fa-solid fa-file-pdf text-danger text-xl" /><span className="text-sm underline">{msg.fileName || 'Document.pdf'}</span></a>}
        {msg.type === 'voice'  && <audio controls src={msg.fileUrl} className="h-8 max-w-full" />}
        {msg.type === 'system' && <p className="text-xs italic opacity-80">{msg.message}</p>}
        <p className={`text-[9px] mt-1 text-right ${isMe ? 'text-white/70' : 'text-text-muted'}`}>
          {t}{isMe && <i className={`fa-solid fa-check-double ml-1 ${msg.isRead ? 'text-blue-300' : 'opacity-60'}`} />}
        </p>
      </div>

      {/* Context menu for own messages */}
      {isMe && menuOpen && (
        <div ref={menuRef} className={`absolute z-50 bottom-full mb-1 ${isMe ? 'right-0' : 'left-0'} bg-white rounded-2xl shadow-xl border border-border overflow-hidden min-w-[180px]`}>
          <button
            onClick={() => setMenuOpen(false)}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-text-secondary hover:bg-surface">
            <i className="fa-solid fa-xmark" /> Cancel
          </button>
        </div>
      )}
    </div>
  );
}

function DateSep({ date }) {
  return (
    <div className="flex items-center gap-2 my-3">
      <div className="flex-1 h-px bg-border" />
      <span className="text-[10px] text-text-muted px-2">
        {new Date(date).toLocaleDateString('en', { weekday: 'long', day: 'numeric', month: 'short' })}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}


// Call UI (incoming modal + active call screen) now lives entirely in
// <GlobalCallOverlay/>, shared globally via CallContext — removed the
// duplicate IncomingCallModal/CallScreen components that used to live here.


export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate           = useNavigate();
  const location           = useLocation();
  const { user }           = useAuth();
  const { socket }         = useSocket();

  // Derive the remote user ID from conversationId
  // Normal conv: [patientId, doctorId].sort().join('_')  → two 24-char hex IDs
  // Store conv:  [patientId, 'store_doctorId'].sort().join('_') → contains 'store_'
  const myId = user?._id?.toString() || '';
  const remoteUserId = (() => {
    if (!conversationId || !myId) return '';
    // Store conversation — remote is 'store_doctorId'
    if (conversationId.includes('store_')) {
      // Find the store_ part
      const storeMatch = conversationId.match(/(store_[a-f0-9]{24})/i);
      if (storeMatch) return storeMatch[1]; // e.g. 'store_abc123'
    }
    // Normal conversation
    const parts = conversationId.split('_');
    return parts.find(p => p !== myId && p.length === 24) || '';
  })();

  const [messages, setMessages]       = useState([]);
  const [text, setText]               = useState('');
  const [isTyping, setIsTyping]       = useState(false);
  const [theyTyping, setTheyTyping]   = useState(false);
  const [loading, setLoading]         = useState(true);
  const [isReadOnly, setIsReadOnly]   = useState(false);
  const [slotStatus, setSlotStatus]   = useState(null);
  const [showReview, setShowReview]   = useState(false);
  const [showAttach, setShowAttach]   = useState(false);
  const [uploading, setUploading]     = useState(false);
  const bottomRef  = useRef(null);
  const typingTimer = useRef(null);
  const fileRef    = useRef(null);

  const appointmentId = location.state?.appointmentId || null;
  // Doctor's display name — prefer nav state (passed from ChatListPage),
  // fall back to slot-status response (populated doctorId.name from backend).
  const doctorDisplayName = location.state?.doctorName || slotStatus?.doctorName || 'Doctor';

  // Timer for video consult — use IST-aware date extraction
  const slotDuration = slotStatus?.duration || 10;
  const slotTimerTime = slotStatus?.slotStart
    ? (() => {
        // slotStart is UTC ISO string from backend — convert to IST before extracting HH:MM
        const istMs = new Date(slotStatus.slotStart).getTime() + 5.5 * 60 * 60 * 1000;
        const istDate = new Date(istMs);
        return `${String(istDate.getUTCHours()).padStart(2,'0')}:${String(istDate.getUTCMinutes()).padStart(2,'0')}`;
      })()
    : null;
  const slotTimerDate = slotStatus?.slotStart
    ? (() => {
        const istMs = new Date(slotStatus.slotStart).getTime() + 5.5 * 60 * 60 * 1000;
        return new Date(istMs).toISOString().split('T')[0];
      })()
    : null;
  const slotTimer = useSlotTimer(slotTimerTime, slotTimerDate, slotDuration);

  // WebRTC — single SHARED global call engine (same instance GlobalCallOverlay
  // uses). startCall now needs the targetId passed explicitly per call.
  const webrtc = useCall();

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getChatHistory(conversationId, { limit: 50 });
      setMessages(res.messages || []);
    } catch {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [conversationId]);

  // Check slot status (for video chats)
  const checkSlotStatus = useCallback(async () => {
    if (!appointmentId) return;
    try {
      const res = await getSlotStatus(appointmentId);
      const wasActive  = slotStatus?.status === 'active';
      const nowExpired = res.status === 'expired';
      setSlotStatus(res);
      setIsReadOnly(nowExpired);
      if (wasActive && nowExpired) {
        // Slot just expired: end call + show review
        if (webrtc.callState !== 'idle') {
          webrtc.endCall();
          toast.error('Slot time ended — consultation closed');
        }
        setTimeout(() => setShowReview(true), 2000);
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, slotStatus?.status, webrtc.callState]);

  useEffect(() => {
    loadHistory();
    checkSlotStatus();
    // Poll slot status every 15s for accurate expiry
    const interval = setInterval(checkSlotStatus, 15000);
    return () => clearInterval(interval);
  }, [loadHistory, checkSlotStatus]);

  // Watch slotTimer — instant lock + call-end when countdown hits 0
  useEffect(() => {
    if (slotTimer.status === 'expired') {
      setIsReadOnly(true);
      if (webrtc.callState !== 'idle') {
        webrtc.endCall();
        toast.error('Slot time ended — consultation closed automatically');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotTimer.status]);

  // Scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Socket events
  useEffect(() => {
    if (!socket) return;

    const onReceive = (msg) => {
      // Strict: only accept messages for THIS conversation
      // Do NOT use user._id check — that would pull store/other chat messages here
      if (msg.conversationId === conversationId) {
        setMessages((prev) => [...prev, { ...msg, isRead: false }]);
        socket.emit('mark-read', { conversationId });
      }
    };

    const onTyping    = ({ from }) => { if (from === remoteUserId) setTheyTyping(true); };
    const onStopTyping = ({ from }) => { if (from === remoteUserId) setTheyTyping(false); };
    const onMsgRead   = () => setMessages((prev) => prev.map((m) => ({ ...m, isRead: true })));
    const onSlotDelay = ({ bookingId }) => {
      if (appointmentId || bookingId) checkSlotStatus();
    };

    socket.on('receive-message',       onReceive);
    socket.on('user-typing',           onTyping);
    socket.on('user-stopped-typing',   onStopTyping);
    socket.on('messages-read',         onMsgRead);
    socket.on('slot-delay-updated',    onSlotDelay);

    // Mark existing as read
    socket.emit('mark-read', { conversationId });

    return () => {
      window.__chatPageHandlingCall = null;
      socket.off('receive-message',     onReceive);
      socket.off('user-typing',         onTyping);
      socket.off('user-stopped-typing', onStopTyping);
      socket.off('messages-read',       onMsgRead);
      socket.off('slot-delay-updated',  onSlotDelay);
    };
  }, [socket, conversationId, remoteUserId, user?._id, appointmentId, checkSlotStatus]);

  // Send text message
  const handleDeleteForEveryone = React.useCallback(async (messageId) => {
    try {
      await deleteChatMessage(messageId);
      setMessages(prev => prev.map(m =>
        m._id === messageId
          ? { ...m, deletedForEveryone: true, message: '', fileUrl: null }
          : m
      ));
    } catch (err) {
      const expired = err.response?.data?.expired;
      import('react-hot-toast').then(m =>
        m.default.error(expired
          ? '5-minute window has passed — cannot delete for everyone'
          : 'Could not delete message')
      );
    }
  }, []);

  const sendMessage = () => {
    if (!text.trim() || isReadOnly || !socket) return;
    const tempId = Date.now().toString();
    const msg = {
      _id: tempId, conversationId,
      from: user._id, senderId: user._id,
      message: text, type: 'text',
      timestamp: new Date().toISOString(), isRead: false,
    };
    setMessages((prev) => [...prev, msg]);
    if (!remoteUserId) { toast.error('Chat not available — please go back and try again'); return; }
    socket.emit('send-message', { to: remoteUserId, message: text, type: 'text', appointmentId });
    setText('');
    setShowAttach(false);
  };

  // Typing indicator
  const handleTextChange = (val) => {
    setText(val);
    if (!isReadOnly && socket) {
      if (!isTyping) { socket.emit('typing', { to: remoteUserId }); setIsTyping(true); }
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => {
        socket.emit('stop-typing', { to: remoteUserId });
        setIsTyping(false);
      }, 1500);
    }
  };

  // File upload
  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setShowAttach(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('receiverId', remoteUserId);
      if (appointmentId) fd.append('appointmentId', appointmentId);
      const res = await uploadChatFile(fd);
      setMessages((prev) => [...prev, { ...res.message, from: user._id, timestamp: new Date().toISOString() }]);
    } catch (err) {
      toast.error('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((acc, msg) => {
    const date = new Date(msg.timestamp || msg.createdAt).toDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(msg);
    return acc;
  }, {});

  const slotActive   = slotStatus?.status === 'active';
  const slotWaiting  = slotStatus?.status === 'waiting';
  const slotExpired  = slotStatus?.status === 'expired';
  // chatDisabled for patient: only active during slot window
  // Before slot: waiting (read-only), After slot: expired (read-only)
  // eslint-disable-next-line no-unused-vars
  const isClinicAppt  = slotStatus?.appointmentType === 'clinic';
  // eslint-disable-next-line no-unused-vars
  const isVideoAppt   = slotStatus?.appointmentType === 'video' || !slotStatus?.appointmentType;
  // Clinic visits: chat always open (isTimeLocked=false from backend)
  // Video visits: time-gated (isTimeLocked=true, must be in active slot window)
  const chatDisabled  = isReadOnly ||
    (slotStatus?.isTimeLocked && !slotActive);

  return (
    <div className="flex flex-col bg-white w-full" style={{height:"100dvh",maxWidth:"768px",margin:"0 auto",borderLeft:"1px solid #E2E8F0",borderRight:"1px solid #E2E8F0"}}>
      {/* ── Header ──────────────────────────────────────── */}
      <div className="sticky top-0 z-30 h-14 bg-white border-b border-border flex items-center px-4 gap-3 flex-shrink-0">
        <button onClick={() => navigate(-1)} className="btn-icon">
          <i className="fa-solid fa-arrow-left text-text-primary" />
        </button>
        <div className="w-9 h-9 rounded-full bg-primary-light flex items-center justify-center flex-shrink-0">
          <i className="fa-solid fa-user-doctor text-primary text-sm" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">{doctorDisplayName}</p>
          {theyTyping
            ? <p className="text-xs text-success">typing...</p>
            : <p className="text-xs text-text-muted">{slotStatus?.appointmentType === 'video' ? 'Video Consultation' : 'General Chat'}</p>}
        </div>
        {/* Only show call buttons when chat is active */}
        {!chatDisabled && (
          <div className="flex gap-1">
            <button onClick={() => webrtc.startCall('audio', user?.name || 'Patient', remoteUserId, false, doctorDisplayName)}
              className="btn-icon">
              <i className="fa-solid fa-phone text-primary text-base" />
            </button>
            <button onClick={() => webrtc.startCall('video', user?.name || 'Patient', remoteUserId, false, doctorDisplayName)}
              className="btn-icon">
              <i className="fa-solid fa-video text-info text-base" />
            </button>
          </div>
        )}
      </div>

      {/* ── Video slot countdown banner ──────────────────── */}
      {slotStatus && (
        <div className={`fixed top-14 left-0 right-0 z-20 px-4 py-2 text-center text-xs font-semibold ${
          slotActive ? 'bg-success text-white' : slotTimer.status === 'waiting' ? 'bg-amber-50 text-amber-700 border-b border-amber-200' : 'bg-gray-100 text-text-muted'
        }`}>
          {slotActive
            ? <span><i className="fa-solid fa-circle text-xs mr-1.5 animate-pulse" />Consultation Active — {formatCountdown(slotTimer.remaining)} remaining</span>
            : slotWaiting
            ? <span><i className="fa-solid fa-clock mr-1.5" />Slot starts in {formatCountdown(slotTimer.remaining)}</span>
            : <span><i className="fa-solid fa-lock mr-1.5" />Consultation ended — read only</span>}
        </div>
      )}

      {/* ── Messages area ───────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-8 h-8 border-2 border-primary-light border-t-primary rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
            <div className="w-14 h-14 bg-primary-light rounded-full flex items-center justify-center">
              <i className="fa-solid fa-comment-medical text-primary text-xl" />
            </div>
            <p className="text-sm text-text-muted">No messages yet.<br />Say hello!</p>
          </div>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <div key={date}>
              <DateSep date={date} />
              {msgs.map((msg, i) => (
                <MessageBubble
                  key={msg._id || i}
                  msg={msg}
                  isMe={msg.from === user?._id || msg.senderId === user?._id}
                  onDeleteForEveryone={handleDeleteForEveryone}
                />
              ))}
            </div>
          ))
        )}
        {theyTyping && (
          <div className="flex gap-1 items-center px-3 py-2 bg-white rounded-2xl rounded-bl-sm w-16 shadow-sm border border-border mb-2">
            {[0,1,2].map((i) => (
              <span key={i} className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce" style={{ animationDelay: `${i*0.15}s` }} />
            ))}
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Read-only notice ─────────────────────────────── */}
      {/* Only show lock banner for VIDEO (time-locked) appointments */}
      {chatDisabled && slotStatus?.isTimeLocked && (
        <div className={`px-4 py-3 border-t text-center ${slotExpired ? 'bg-gray-50 border-gray-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center justify-center gap-2">
            <i className={`fa-solid ${slotExpired ? 'fa-lock' : 'fa-clock'} text-sm ${slotExpired ? 'text-gray-500' : 'text-amber-600'}`} />
            <p className={`text-xs font-semibold ${slotExpired ? 'text-gray-700' : 'text-amber-800'}`}>
              {slotExpired
                ? '⏱ Video slot ended — chat is read-only. Book again to continue.'
                : slotWaiting
                ? '⏳ Chat unlocks when your video slot begins'
                : 'This chat is read-only'}
            </p>
          </div>
          <p className={`text-[10px] mt-0.5 ${slotExpired ? 'text-gray-500' : 'text-amber-600'}`}>
            {slotExpired
              ? 'You can view your conversation history below'
              : 'You will be able to message once your video slot begins'}
          </p>
        </div>
      )}

      {/* ── Attachment menu ──────────────────────────────── */}
      {showAttach && !chatDisabled && (
        <>
          <div className="backdrop" onClick={() => setShowAttach(false)} />
          <div className="fixed bottom-20 left-4 right-4 z-40 bg-white rounded-2xl shadow-xl border border-border p-4">
            <div className="grid grid-cols-3 gap-3">
              {[
                { icon: 'fa-image',       label: 'Photo',    accept: 'image/*',       color: 'bg-blue-100 text-info' },
                { icon: 'fa-file-pdf',    label: 'Document', accept: '.pdf',           color: 'bg-red-100 text-danger' },
                { icon: 'fa-microphone',  label: 'Audio',    accept: 'audio/*',        color: 'bg-green-100 text-success' },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    fileRef.current.accept = item.accept;
                    fileRef.current.click();
                  }}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-surface"
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${item.color}`}>
                    <i className={`fa-solid ${item.icon} text-lg`} />
                  </div>
                  <span className="text-xs text-text-secondary">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      <input ref={fileRef} type="file" className="hidden"
        onChange={(e) => { handleFileUpload(e.target.files[0]); e.target.value = ''; }} />

      {/* ── Input bar ────────────────────────────────────── */}
      {!chatDisabled && (
        <div className="bg-white border-t border-border px-3 py-2 safe-bottom">
          <div className="flex items-end gap-2">
            <button
              onClick={() => setShowAttach(!showAttach)}
              className="btn-icon flex-shrink-0 mb-0.5"
            >
              {uploading
                ? <i className="fa-solid fa-spinner animate-spin text-primary" />
                : <i className="fa-solid fa-plus text-text-secondary text-lg" />}
            </button>
            <div className="flex-1 bg-surface border border-border rounded-2xl px-4 py-2.5 min-h-[42px] flex items-center">
              <textarea
                rows={1}
                value={text}
                onChange={(e) => handleTextChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
                }}
                placeholder="Type a message..."
                className="flex-1 bg-transparent resize-none outline-none text-sm text-text-primary placeholder:text-text-muted max-h-24"
                style={{ lineHeight: '1.4' }}
              />
            </div>
            <button
              onClick={sendMessage}
              disabled={!text.trim()}
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                text.trim() ? 'bg-primary' : 'bg-gray-100'
              }`}
            >
              <i className={`fa-solid fa-paper-plane text-sm ${text.trim() ? 'text-white' : 'text-text-muted'}`} />
            </button>
          </div>
        </div>
      )}

      {/* Call UI is now rendered globally by <GlobalCallOverlay/> (mounted once
          at App root) using the SAME shared webrtc instance — so the exact
          same call UI shows up whether this chat page is open or not,
          WhatsApp-style. No duplicate call screen rendered here anymore. */}

      {/* Review popup — shown after video consultation ends */}
      {showReview && (
        <ReviewModal
          appointment={{ _id: appointmentId }}
          doctor={slotStatus?.appointmentData?.doctorId}
          onClose={() => setShowReview(false)}
          onDone={() => setShowReview(false)}
        />
      )}
    </div>
  );
}
